/**
 * Supabase persistence for the change log and agent conversations.
 *
 * Both are append-mostly, which is why neither uses the whole-store diffing
 * the entity adapters do: a change entry is written once and then only ever
 * marked reverted, and a conversation grows by appending messages. Upserting
 * the rows that changed is both simpler and closer to how they are used.
 *
 * The localStorage versions capped their size purely because of the ~5MB
 * quota -- articles/history.ts trims entries, then re-trims to a "lean" form
 * when even that overflows. Those caps do not apply here, which is much of
 * the point: an audit trail that silently discards its oldest entries is not
 * much of an audit trail.
 */

import { cmsDb } from "./client"
import type { Backend } from "./types"

const HISTORY_TABLE = "scms_change_entry"

// Rows that hold unpublished changes (staging.ts) and record details (recordMeta.ts), not history
export const PENDING_ID_PREFIX = "pending."
export const META_ID_PREFIX = "meta."
const CONVERSATION_TABLE = "scms_conversation"

/** How many entries a single history read pulls. */
const HISTORY_PAGE = 500

/*
 * The client's ChangeEntry, structurally. Imported as a shape rather than a
 * type because the three backends each declare their own (identical) version
 * and none of them is canonical.
 */
interface ChangeEntryLike {
  id: string
  at: number
  action: string
  actor?: "agent" | "user"
  authorName?: string
  authorEmail?: string
  authorId?: string
  viaModel?: string
  entityKind?: string
  entityId?: string
  entityName?: string
  locale?: string
  summary?: string
  before?: Record<string, unknown>
  after?: Record<string, unknown>
  reverted?: boolean
}

function entryToRow(e: ChangeEntryLike, backend: Backend, teamId: string) {
  return {
    id: e.id,
    team_id: teamId,
    backend,
    at_ms: e.at,
    action: e.action,
    actor: e.actor ?? "user",
    author_name: e.authorName ?? null,
    author_email: e.authorEmail ?? null,
    // The column is uuid; a non-uuid author id would fail the insert and take
    // the whole history write with it, so anything unparseable is dropped.
    author_id: isUuid(e.authorId) ? e.authorId : null,
    via_model: e.viaModel ?? null,
    entity_kind: e.entityKind ?? null,
    entity_id: e.entityId ?? null,
    entity_name: e.entityName ?? "",
    locale: e.locale ?? null,
    summary: e.summary ?? "",
    before: e.before ?? null,
    after: e.after ?? null,
    reverted: e.reverted ?? false,
  }
}

function rowToEntry(r: any): ChangeEntryLike {
  const e: ChangeEntryLike = {
    id: r.id,
    at: Number(r.at_ms),
    action: r.action,
    actor: r.actor,
    entityName: r.entity_name ?? "",
    summary: r.summary ?? "",
    reverted: r.reverted ?? false,
  }
  // Absent rather than null: the client model uses optional fields, and a
  // literal null would defeat `?? fallback` checks downstream.
  if (r.author_name) e.authorName = r.author_name
  if (r.author_email) e.authorEmail = r.author_email
  if (r.author_id) e.authorId = r.author_id
  if (r.via_model) e.viaModel = r.via_model
  if (r.entity_kind) e.entityKind = r.entity_kind
  if (r.entity_id) e.entityId = r.entity_id
  if (r.locale) e.locale = r.locale
  if (r.before) e.before = r.before
  if (r.after) e.after = r.after
  return e
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
function isUuid(v: unknown): v is string {
  return typeof v === "string" && UUID_RE.test(v)
}

export function makeHistoryAdapter(backend: Backend, teamId: string) {
  return {
    /** Newest first, capped. Matches how the history panel reads it. */
    async load(): Promise<ChangeEntryLike[]> {
      const { data, error } = await cmsDb
        .from(HISTORY_TABLE)
        .select("*")
        .eq("team_id", teamId)
        .eq("backend", backend)
        .not("id", "like", `${PENDING_ID_PREFIX}%`)
        .not("id", "like", `${META_ID_PREFIX}%`)
        .order("at_ms", { ascending: false })
        .limit(HISTORY_PAGE)
      if (error) throw error
      // The client keeps entries oldest-first; the query is newest-first so
      // the LIMIT keeps recent history rather than ancient history.
      return (data ?? []).map(rowToEntry).reverse()
    },

    /**
     * Upserts the given entries. Called with the whole in-memory log, which is
     * already capped client-side, so this is a bounded write rather than a
     * growing one.
     */
    async save(entries: ChangeEntryLike[]): Promise<void> {
      if (!entries.length) return
      const rows = entries.map(e => entryToRow(e, backend, teamId))
      const { error } = await cmsDb.from(HISTORY_TABLE).upsert(rows)
      if (error) throw error
    },

    async clear(): Promise<void> {
      const { error } = await cmsDb
        .from(HISTORY_TABLE).delete()
        .eq("team_id", teamId).eq("backend", backend)
        .not("id", "like", `${PENDING_ID_PREFIX}%`)
        .not("id", "like", `${META_ID_PREFIX}%`)
      if (error) throw error
    },
  }
}

/* ── Conversations ──────────────────────────────────────────────────────── */

interface ConversationLike {
  id: string
  title?: string
  messages?: unknown[]
  createdAt?: number
  updatedAt?: number
}

export function makeConversationAdapter(backend: string, teamId: string) {
  return {
    async list(): Promise<ConversationLike[]> {
      const { data, error } = await cmsDb
        .from(CONVERSATION_TABLE)
        .select("*")
        .eq("team_id", teamId)
        .eq("backend", backend)
        .order("updated_at_ms", { ascending: false })
        .limit(100)
      if (error) throw error
      return (data ?? []).map((r: any) => ({
        id: r.id,
        title: r.title,
        messages: r.messages ?? [],
        createdAt: Number(r.created_at_ms),
        updatedAt: Number(r.updated_at_ms),
      }))
    },

    async save(c: ConversationLike): Promise<void> {
      const now = Date.now()
      const { error } = await cmsDb.from(CONVERSATION_TABLE).upsert({
        id: c.id,
        team_id: teamId,
        backend,
        title: c.title ?? "New conversation",
        messages: c.messages ?? [],
        created_at_ms: c.createdAt ?? now,
        updated_at_ms: c.updatedAt ?? now,
      })
      if (error) throw error
    },

    async remove(id: string): Promise<void> {
      const { error } = await cmsDb.from(CONVERSATION_TABLE).delete().eq("id", id)
      if (error) throw error
    },
  }
}
